import { Context } from '../../common/context.js';
import { OuterParams } from './outer.js';
import { Icon } from '../components/icons.js';
import { FaqCategory, Maybe } from '../../common/data.js';
import { ensure, isIn, isSome, keepExisting, lit, logRejection, maybe, within } from '../../common/util.js';
import { Cart, expandCart, ExpandedCart } from '../../common/data/cart.js';
import { Block, ContentExtras } from '../../common/data/content.js';
import { ContentBlock, ContentContext } from '../components/content.js';
import { AboveHeader, checkIfDesktop, Header, Sidebar } from '../components/site-nav.js';
import { cleanUpDomain } from '../../common/data/clean.js';
import { Menu, MenuEntry } from '../../common/data/menus.js';
import { ComponentChild, ComponentChildren, ComponentFactory, h } from 'preact';
import { Store, Tier } from '../../common/data/stores.js';
import { VendorUser } from '../../common/data/vendors.js';
import { designalize, signalize, SiteContext, SiteContextInit } from '../context.js';
import { AccountAccessData } from '../../common/data/accounts.js';
import { useEffect } from 'preact/hooks';
import { makeUserMenus } from '../components/dashboard/menus.js';
import { UnreadMessageCounts } from '../../common/data/messages.js';
import { getCart, getContextUpdates } from '../lib/api.js';
import { bumpServiceWorker, setUpServiceWorker } from '../lib/sw-glue.js';
import { batch, computed, effect, signal } from '@preact/signals';

export interface CommonSiteParams {
	noFooter: boolean;
	siteClass: Maybe<string[]>;
	sidebarLocked: Maybe<boolean>;
	doubleHeaderAllowed: Maybe<boolean>;
}

export const defaultSiteParams: CommonSiteParams = {
	doubleHeaderAllowed: true,
	noFooter: false,
	siteClass: null,
	sidebarLocked: null,
};

export interface SiteParams extends CommonSiteParams {
	body: ComponentChild;
}

export interface SiteData extends CommonSiteParams {

	cart: Maybe<Cart>;

	pathname: string;
	query: Maybe<Record<string, string>>;
	doubleHeader: boolean;
	siteClass: Maybe<string[]>;
	sidebarLocked: boolean;

	mainMenu: Menu;
	faqCategories: FaqCategory[];
	footer: Maybe<Block>;
	extras: ContentExtras;
	
	accountAccess: Maybe<AccountAccessData>;
	accountTier: Maybe<Tier>;
	accountStores: Maybe<Store[]>;
	
	accounts: Maybe<AccountAccessData[]>;
	vendorUsers: Maybe<VendorUser[]>;
	unreadMessageCounts: Maybe<UnreadMessageCounts>;
	
	stateHash: Maybe<string>;

}

export function createSiteBody(c: Context, params: OuterParams & SiteParams, data: SiteData) {

	const { domain, user, account } = c;

	// TODO verify message instead!
	// const badEmail = user && !user.email?.match(emailRegex);
	
	return <SiteBody
		siteContext={createSiteContext({
			...data,
			context: c,
			domain,
			user,
			account,
			cart: maybe(data.cart, expandCart),
		})}
		pageBody={params.body}
	/>;

}

let currentSiteContext: Maybe<SiteContext> = null;
export function createSiteContext(init: SiteContextInit) {
	
	const sigs = signalize(init);
	
	const {
		domain,
		user,
		account,
		accounts,
		accountAccess,
		vendorUsers,
		accountStores,
		sidebarLocked,
		unreadMessageCounts,
		stateHash,
	} = sigs;
	
	const auxMenu = computed(() => ({
		entries: keepExisting([
			{
				title: 'Help',
				link: '/faq',
				icon: <Icon name="faq" />,
			},
			{
				title: 'Cart',
				link: '/cart',
				icon: <Icon name="cart2" />,
				tags: ['cart'],
			},
			maybe(domain.value.phone_number, p => ({
				title: p,
				link: `tel:${p}`,
				icon: <Icon name="phone-short" />,
				tags: ['mobile-only'],
			})),
			!user.value && {
				title: 'Log in',
				link: '/auth',
				icon: <Icon name="sign-in" />,
			} || null,
			!user.value && {
				title: 'Sign up',
				link: '/auth/sign-up',
				icon: <Icon name="sign-up" />,
			} || null,
			user.value && {
				title: 'Log out',
				icon: <Icon name="lock" />,
				tags: ['log-out'],
			},
		] as Maybe<MenuEntry>[]),
	}));
	const userMenus = computed(() => makeUserMenus(
		designalize({ user, vendorUsers, account, accountStores, accountAccess })
	));
	
	const isDesktop = signal(checkIfDesktop());
	const sidebarOpen = signal(sidebarLocked.peek() && isDesktop.peek());
	
	const toggleSidebar = (e: Event) => {
		if ((e as KeyboardEvent).key && !isIn((e as KeyboardEvent).key, ['Enter', ' '])) return;
		e.preventDefault();
		const val = (sidebarOpen.value = !sidebarOpen.peek());
		if (val && e instanceof KeyboardEvent && !(e.target as HTMLElement).closest('.sidebar')) {
			setTimeout(() => {
				const sb = ensure(document.querySelector<HTMLElement>('.sidebar'));
				sb.scrollTo(0, 0);
				sb.querySelector<HTMLElement>('.header-logo__menu')?.focus();
			}, 100);
		}
	};
	const openSidebar = (e?: Event) => {
		e?.preventDefault();
		sidebarOpen.value = true;
	};
	const closeSidebar = (e?: Event) => {
		if (e && (e as KeyboardEvent).key && !isIn((e as KeyboardEvent).key, ['Enter', ' ', 'Esc'])) return;
		e?.preventDefault();
		sidebarOpen.value = false;
	};
	
	const fetchUpdates = async () => {
		const hash = stateHash.peek();
		if (!user.peek() || !hash) return;
		const { counts, state } = await getContextUpdates({ stateHash: hash });
		batch(() => {
			unreadMessageCounts.value = counts;
			if (state) {
				user.value = state.user;
				account.value = state.account;
				accountAccess.value = state.accountAccess;
				accounts.value = state.accounts;
				accountStores.value = state.accountStores;
				stateHash.value = state.stateHash;
			}
		});
	};
	
	const setActiveCart = async (cart: ExpandedCart) => {
		sigs.cart.value = cart;
		localStorage.setItem(`${cart.account}_cart`, cart.id);
		await bumpServiceWorker(sc);
	};
	
	const sc: SiteContext = {
	
		...sigs,
		
		userMenus,
		auxMenu,
		isDesktop,
		sidebarOpen,
		scrolled: signal(false),
		
		toggleSidebar,
		openSidebar,
		closeSidebar,
		
		fetchUpdates,
		setActiveCart,
		
	};
	
	if (typeof window == 'object') currentSiteContext = sc;
	
	return sc;
	
}
export const getSiteContext = () => currentSiteContext;


export const getFallbackContext = () => (typeof window != 'undefined' ? window as any : null)?.siteContext?.context as Maybe<Context>;

const menuNesting = lit([
	['.header-top-menu__item--submenu', '.header-top-menu__item--submenu'],
]);

interface RankEntry {
	el: HTMLElement;
	rect: DOMRect;
	score: number;
	scoreX: number;
	scoreY: number
}

export function SiteBody({ siteContext, pageHandler, pageData, pageBody }: {
	siteContext: SiteContext;
	pageHandler?: ComponentFactory<any>;
	pageBody?: ComponentChildren;
	pageData?: any;
}) {
	
	if (typeof document == 'object') effect(() => {
		const cl = document.body.classList;
		cl.toggle('site--sidebar-locked', siteContext.sidebarLocked.value);
		cl.toggle('site--sidebar-shown', siteContext.sidebarOpen.value || false);
		cl.toggle('site--scrolled', siteContext.scrolled.value);
	});
	
	if (typeof document == 'object') useEffect(() => {
	
		setUpServiceWorker(siteContext).catch(logRejection);
	
		window.addEventListener('resize', () => {
			siteContext.isDesktop.value = checkIfDesktop();
		});
		
		const checkScroll = () => {
			siteContext.scrolled.value = document.documentElement.scrollTop > 50;
		};
		window.addEventListener('scroll', checkScroll);
		checkScroll();
		
		const body = document.body;
		
		setTimeout(() => {
			body.classList.add('site--ready');
			body.classList.remove('site--init');
			checkScroll();
		}, 1);
		
		window.addEventListener('focus', async () => {
			const account = siteContext.account.peek();
			if (account) {
				const selCart = localStorage.getItem(`${account.id}_cart`);
				if (selCart && selCart != siteContext.cart.peek()?.id) {
					await getCart({ id: selCart })
						.then(({ cart }) => siteContext.setActiveCart(expandCart(cart)))
						.catch(e => {
							console.error(e);
							localStorage.removeItem(`${account.id}_cart`);
						});
				}
			}
			await bumpServiceWorker(siteContext);
			await siteContext.fetchUpdates();
		});
		setInterval(siteContext.fetchUpdates, 600_000);
		
		const focusSel = 'a, button, input, textarea, label, select, details';
		const sharedAreaSel = '.header, .above-header';
		const arrowKeys = lit(['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft']);
		const navKeys = lit(['Tab', 'Escape', 'Enter', ' ', ...arrowKeys]);
		let lastArrowFocus: Maybe<Element> = null;
		let hitEdgeAt: Maybe<number> = Date.now();
		let hitEdgeWith: Maybe<string> = null;
		let lastRanked: Maybe<RankEntry[]> = [];
		body.addEventListener('keydown', e => {
			const key = e.key;
			if (!isIn(key, arrowKeys)) lastArrowFocus = null;
			if (!isIn(key, navKeys) || e.defaultPrevented || e.altKey || e.ctrlKey || e.shiftKey) return;
			const t = performance.now();
			const tgEl = (e.currentTarget instanceof HTMLElement && e.currentTarget.closest(focusSel)) ?? document.activeElement?.closest(focusSel) ?? document.querySelector(focusSel);
			if (!tgEl) return;
			const isSelect = tgEl instanceof HTMLSelectElement;
			const isInput = tgEl instanceof HTMLTextAreaElement || (tgEl instanceof HTMLInputElement && !isIn(tgEl.type, ['checkbox']));
			const mayHandleArrows = isSelect || isInput;
			const kbdArea = tgEl.closest<HTMLElement>('.kbd-area');
			const sameKbdArea = (el: HTMLElement) => {
				const area = el.closest<HTMLElement>('.kbd-area');
				return (area == kbdArea) || (area?.matches(sharedAreaSel) && kbdArea?.matches(sharedAreaSel));
			};
			const hitEdge = hitEdgeWith == key && isSome(hitEdgeAt) && Date.now() - hitEdgeAt > 50;
			if (isIn(key, arrowKeys) && (!mayHandleArrows || lastArrowFocus == tgEl)) {
				const tgRect = tgEl.getBoundingClientRect();
				const aLeft = key == 'ArrowLeft', aUp = key == 'ArrowUp', aRight = key == 'ArrowRight', aDown = key == 'ArrowDown';
				const oX = tgRect.left + tgRect.width / 2;
				const oY = tgRect.top + tgRect.height / 2;
				const minX = aRight ? oX : -Infinity;
				const maxX = aLeft ? oX : Infinity;
				const minY = aDown ? oY : -Infinity;
				const maxY = aUp ? oY : Infinity;
				const mulX = aRight ? 1 : aLeft ? -1 : 0;
				const mulY = aDown ? 1 : aUp ? -1 : 0;
				const ranked: RankEntry[] = [];
				for (let el of body.querySelectorAll<HTMLElement>(focusSel)) {
					if (!el.checkVisibility({ visibilityProperty: true }) || el == tgEl || el.contains(tgEl) || tgEl.contains(el)) continue;
					if (
						el instanceof HTMLLabelElement && el.control
						&& el.control.checkVisibility({ visibilityProperty: true })
						&& !(el.control instanceof HTMLInputElement && isIn(el.control.type, ['checkbox', 'radio']))
					) el = el.control;
					if (!hitEdge && !sameKbdArea(el)) continue;
					const rect = el.getBoundingClientRect();
					if (rect.left < minX || rect.right > maxX || rect.top < minY || rect.bottom > maxY) continue;
					const dX = aRight ? rect.left - oX : aLeft ? rect.right - oX : (rect.left + rect.width / 2) - oX;
					const dY = aDown ? rect.top - oY : aUp ? rect.bottom - oY : (rect.top + rect.height / 2) - oY;
					let scoreX = 0, scoreY = 0;
					if (mulX) {
						scoreX = dX * mulX;
						scoreY = Math.abs(oY - dY) / Math.max(tgRect.height, rect.height) + Math.max(0, tgRect.top - rect.bottom) * 3 + Math.max(0, rect.top - tgRect.bottom) * 3;
					}
					else {
						scoreY = dY * mulY;
						scoreX = Math.abs(oX - dX) / Math.max(tgRect.width, rect.width) + Math.max(0, tgRect.left - rect.right) * 3 + Math.max(0, rect.left - tgRect.right) * 3;
					}
					let score = scoreX + scoreY;
					// let score = (mulX ? dX * mulX : Math.max(0, Math.abs(dX) - Math.abs(dY)) * 2) + (mulY ? dY * mulY : Math.max(0, Math.abs(dY) - Math.abs(dX)) * 2);
					if (score < 500 && (tgEl.closest('ul')?.closest('li')?.contains(el) || el.closest('li')?.contains(tgEl))) score *= 0.25;
					if (!hitEdge && score > 1000) continue;
					ranked.push({ el, rect, score, scoreX, scoreY });
				}
				ranked.sort((a, b) => a.score - b.score);
				if (localStorage.getItem('arrowDebug') == 'on') {
					const rankedEls = new Set(ranked.map(r => r.el));
					if (lastRanked) for (const { el } of lastRanked) {
						if (!rankedEls.has(el)) el.style.background = '';
					}
					for (const { el, score, scoreX, scoreY } of ranked) {
						const r = 255 - within(0, score, 255);
						const g = 255 - within(0, scoreX, 255);
						const b = 255 - within(0, scoreY, 255);
						el.style.background = `rgba(${r}, ${g}, ${b}, 0.2)`;
						el.setAttribute('data-score', JSON.stringify({ score, scoreX, scoreY }));
					}
					lastRanked = ranked;
				}
				const cur = document.activeElement;
				// Try focusing next best until it actually focuses
				let justScroll = false;
				for (const [i, { el, rect, score }] of ranked.entries()) {
					
					// if (!navAllowed && navCtr?.contains(el)) continue;
					// Defer to browser to just scroll instead.
					if (rect.top > window.innerHeight || rect.bottom < 0) {
						justScroll = true;
						break;
					}
					// Attempt focus
					el.focus({ preventScroll: true });
					if (cur == document.activeElement) continue;
					if (rect.bottom > window.innerHeight || rect.top < 0) {
						el.scrollIntoView({ behavior: 'smooth' });
					}
					break;
				}
				const fin = document.activeElement;
				if (cur != fin) {
					e.preventDefault();
					lastArrowFocus = fin;
					hitEdgeAt = null;
					hitEdgeWith = null;
					if (fin && kbdArea && !sameKbdArea(fin as HTMLElement) ) {
						kbdArea.dispatchEvent(new Event('keyboardleave'));
					}
				}
				else if (fin instanceof HTMLSelectElement && lastArrowFocus == fin) {
					e.preventDefault(); // Prevent surprise <select> changes while navigating.
					e.stopPropagation();
				}
				else if (hitEdgeWith != key && !justScroll) {
					hitEdgeWith = key;
					hitEdgeAt = Date.now();
				}
				else if (aUp && kbdArea && hitEdgeWith == key && tgEl.closest('.site:not(.site--sidebar-locked) .sidebar--visible .sidebar__header')) {
					document.querySelector<HTMLElement>('.header-logo__menu')?.focus();
					kbdArea.dispatchEvent(new Event('keyboardleave'));
				}
			}
			else if (key == 'Escape') {
				if (!mayHandleArrows) {
					document.querySelector<HTMLElement>('.header-logo__menu')?.focus();
					document.querySelector<HTMLElement>('.site:not(.site--sidebar-locked) .sidebar--visible')?.dispatchEvent(new Event('keyboardleave'));
				}
				else {
					lastArrowFocus = tgEl;
				}
			}
		});
		
	}, []);
	
	const { domain, noFooter, footer, extras } = siteContext;
	return <SiteContext.Provider value={siteContext}>
		
		{/*badEmail && <div class="site__warning"><span>Looks like we didn't get your email address right. Please <a href="/dashboard/settings/">fix it in settings</a> so we can contact you. Thanks!</span><Icon name="info" /></div>)*/}
		
		<div class="site-nav">
			
			{siteContext.doubleHeader.value && <AboveHeader />}
			<Header />
			<Sidebar />
			
		</div>
		
		<div class="site__wrapper kbd-area">
			
			{pageBody ? pageBody : pageHandler ? h(pageHandler, { ...pageData, context: siteContext.context.value }) : <div />}
			
			{!noFooter.value && footer.value && <footer className="site__footer">
				<ContentContext.Provider value={{
					target: ['web'],
					domain: cleanUpDomain(domain.value),
					website: `https://${domain.value.host}`,
					extras: extras.value,
					link: s => s,
					image: s => s,
				}}>
					<ContentBlock item={footer.value.content} />
				</ContentContext.Provider>
			</footer>}
		
		</div>
	
	</SiteContext.Provider>;
	
}

export function createSiteDataJSON(json: string) {
	return <script id="site-data" type="application/json" dangerouslySetInnerHTML={{ __html: json }} />;
}
